import { Link, useParams, useSearchParams } from "react-router-dom";
import { useSourceSchedules } from "../hooks/useSourceSchedules";
import { safeReportReturn } from "../lib/analytics-reports";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { AssignedReportCollection } from "../components/analytics/AssignedReportCollection";
import { SourceAnalyticsRefresh } from "../components/analytics/AnalyticsRefreshPanel";
import { ReportSkeleton } from "../components/analytics/ReportSkeleton";
import { WORKSPACE_CONTENT_RAIL_CLASS } from "../lib/run-view-layout";

const SOURCE_TITLES: Partial<Record<string, string>> = {
  "google.analytics.read": "Google Analytics 4",
  "google.search_console.read": "Google Search Console",
  "google.youtube.analytics.read": "YouTube Analytics",
  "meta.facebook_page.analytics.read": "Facebook Page Insights",
  "instagram.analytics.read": "Instagram Insights",
};

/** The brand-scoped schedule projection resolves the assignment before controls mount. */
export function AnalyticsSourceSettingsPage() {
  const { brandId = "", assignmentId = "" } = useParams();
  const [search] = useSearchParams();
  const sources = useSourceSchedules(brandId);
  const capability = search.get("capability");
  // Old links without a capability remain usable only for an unambiguous assignment.
  const matches = sources.data?.data.filter(item => item.assignment_id === assignmentId
    && (capability === null || item.capability === capability)) ?? [];
  const candidate = matches.length === 1 ? matches[0] : undefined;
  // Every supported source can finish its refresh setup here; Data Views links every
  // source type to this page, including Facebook Pages and Instagram.
  const source = candidate?.supported && candidate.capability in SOURCE_TITLES ? candidate : undefined;
  const returnTo = safeReportReturn(search.get("returnTo"), brandId);
  const back = returnTo ?? `/brand/${encodeURIComponent(brandId)}/data-views`;
  return <div className={WORKSPACE_CONTENT_RAIL_CLASS}>
    <WorkspaceHeader eyebrow="Analytics" title="Connection settings" breadcrumbs={[{ label: "Data Views", href: back }, { label: "Connection settings" }]} />
    <Link className="inline-block text-sm text-brand-700 underline" to={back}>← Back to report</Link>
    {sources.isLoading ? <ReportSkeleton label="Loading connection settings…" /> : sources.isError ? <p role="alert">Connection settings could not be loaded.</p> : !source ? <p role="alert">This assigned source is unavailable.</p> : <section key={`${brandId}:${assignmentId}:${source.capability}`} className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">{SOURCE_TITLES[source.capability]}</h2>
      <AssignedReportCollection brandId={brandId} assignmentId={assignmentId} capability={source.capability} returnTo={returnTo} />
      <SourceAnalyticsRefresh brandId={brandId} assignmentId={assignmentId} capability={source.capability} editable reportReturnTo={returnTo ?? undefined} />
      <Link className="inline-block text-sm text-brand-700 underline" to={`/settings/connections/${encodeURIComponent(source.connection_id)}?brandId=${encodeURIComponent(brandId)}`}>Manage account connection</Link>
    </section>}
  </div>;
}
